import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { setExtractedData, setEditableData, setOcrInputID, updateWorkflowSessionState, switchWorkflowSession } from "../store/reducers";
import toast from 'react-hot-toast';
import { getProcessingModalTranslation } from './useLanguage';
// PDFs are now stored only in Redux state (localStorage disabled to avoid quota issues) import { storeMultiplePdfsInSessionStorage } from '../utils/pdfStorage';
import { loadWorkflowOriginalExtractedData, findWorkflowByJobId } from '../utils/workflowSessionStorage';
import { selectActiveTabId } from '../store/selectors/tabSelectors';
import { clearWorkflowFiles } from '../utils/workflowFileCache';

// Helper function to play notification sound
const playNotificationSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a simple beep sound (two-tone chime)
    const playTone = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    
    // Play two-tone chime (success sound)
    playTone(523.25, 0.2, 0); // C5
    playTone(659.25, 0.3, 150); // E5
  } catch (error) {
  }
};

// Helper function to create browser notification
const createNotification = (title, body) => {
  try {
    const notification = new Notification(title, {
      body: body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'document-processing-complete',
      requireInteraction: false,
      silent: false
    });
    
    // Auto-close notification after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
    
    // Focus window when notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
  }
};

// Helper function to show browser notification
const showBrowserNotification = (title, body) => {
  // Check if browser supports notifications
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return;
  }
  
  // Request permission if not already granted
  if (Notification.permission === 'default') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        createNotification(title, body);
      }
    });
  } else if (Notification.permission === 'granted') {
    createNotification(title, body);
  }
};

// One chime + browser notification per job per page load. Hidden streams / SSE reconnect can replay terminal events — refs reset, so dedupe by job id at module scope.
const jobCompletionFeedbackPlayedIds = new Set();

const consumeJobCompletionFeedbackOnce = (completionJobId) => {
  if (completionJobId == null || completionJobId === '') return false;
  const key = String(completionJobId);
  if (jobCompletionFeedbackPlayedIds.has(key)) return false;
  jobCompletionFeedbackPlayedIds.add(key);
  return true;
};

export function useJobStream(jobId) {
  const dispatch = useDispatch();
  const store = useStore();
  const extractedData = useSelector((state) => state.documents.extractedData);
  const isDirty = useSelector((state) => state.documents.isDirty);
  const uploadedFiles = useSelector((state) => state.documents.uploadedFiles || []);
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  const activeTabId = useSelector(selectActiveTabId);
  const [status, setStatus] = useState(null);
  const [thinking, setThinking] = useState("");
  const [text, setText] = useState("");
  const [usage, setUsage] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [currentThinkingSection, setCurrentThinkingSection] = useState("");
  const [sectionKey, setSectionKey] = useState(0); // Track section changes for fade animation
  const abortControllerRef = useRef(null);
  const lastEventTypeRef = useRef('');
  const thinkingBufferRef = useRef("");
  const lastSectionStartRef = useRef(-1); // Track the start position of the current section
  const originalTitleRef = useRef(null);
  const titleRestoreHandlersRef = useRef({ visibility: null, focus: null, click: null });
  
  // OPTIMIZED: Track stored ocrInputID to prevent unnecessary dispatches
  const storedOcrInputIdRef = useRef(null);
  const storedWorkflowSessionIdRef = useRef(null);
  
  // OPTIMIZED: Reset refs when workflow session changes (ensures updates work after switching workflows)
  useEffect(() => {
    if (activeWorkflowSessionId !== storedWorkflowSessionIdRef.current) {
      storedOcrInputIdRef.current = null;
      storedWorkflowSessionIdRef.current = activeWorkflowSessionId;
    }
  }, [activeWorkflowSessionId]);
  
  // Get language preference from cookie
  const getLanguage = () => {
    const cookies = document.cookie.split(';');
    const langCookie = cookies.find(c => c.trim().startsWith('language='));
    return langCookie ? langCookie.split('=')[1] === 'false' : true; // Default to Spanish (true)
  };
  
  // Function to restore original title
  const restoreOriginalTitle = () => {
    if (originalTitleRef.current) {
      document.title = originalTitleRef.current;
      // Clean up event listeners
      if (titleRestoreHandlersRef.current.visibility) {
        document.removeEventListener('visibilitychange', titleRestoreHandlersRef.current.visibility);
        titleRestoreHandlersRef.current.visibility = null;
      }
      if (titleRestoreHandlersRef.current.focus) {
        window.removeEventListener('focus', titleRestoreHandlersRef.current.focus);
        titleRestoreHandlersRef.current.focus = null;
      }
      if (titleRestoreHandlersRef.current.click) {
        document.removeEventListener('click', titleRestoreHandlersRef.current.click);
        titleRestoreHandlersRef.current.click = null;
      }
    }
  };

  useEffect(() => {
    if (!jobId) {
      // Reset states when jobId is cleared
      setStatus(null);
      setThinking("");
      setText("");
      setUsage(null);
      setIsThinking(false);
      setCurrentThinkingSection("");
      setSectionKey(0);
      thinkingBufferRef.current = "";
      lastSectionStartRef.current = -1;
      lastEventTypeRef.current = '';
      originalTitleRef.current = null;
      // Clean up any existing title restore handlers
      restoreOriginalTitle();
      return;
    }

    // Get token from sessionStorage
    const token = sessionStorage.getItem('token');
    
    // Detect if this is a demo job (demo jobs start with "job_" and don't require auth)
    const isDemoJob = jobId && jobId.startsWith('job_');
    
    // For demo jobs, use demo endpoint (no auth required)
    // For regular jobs, require token
    if (!isDemoJob && !token) {
      return;
    }

    // Use fetch-based streaming to support Authorization header
    // Demo jobs use /api/demo/queue/{jobId}/stream, regular jobs use /api/queue/{jobId}/stream
    const endpoint = isDemoJob ? `/api/demo/queue/${jobId}/stream` : `/api/queue/${jobId}/stream`;
    const url = `https://${process.env.REACT_APP_DOCS_API_KEY}${endpoint}`;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const streamData = async () => {
      try {
        // Build headers - only include Authorization for non-demo jobs
        const headers = {
          'Accept': 'text/event-stream',
        };
        if (!isDemoJob && token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal: abortController.signal,
        });

        if (!response.ok) {
          // Handle 401 Unauthorized gracefully - don't redirect during active stream
          if (response.status === 401) {
            // Set status to indicate error but don't redirect
            setStatus({
              id: jobId,
              status: 'error',
              message: 'Authentication expired. Please refresh the page.',
              stage: 'stream_error'
            });
            // Abort the stream gracefully
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            return;
          } else {
          return;
          }
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';
        let currentId = '';

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            
            // Handle id line
            if (line.startsWith('id: ')) {
              currentId = line.substring(4).trim();
              continue;
            }
            
            // Handle event line
            if (line.startsWith('event: ')) {
              currentEvent = line.substring(7).trim();
              continue;
            }

            // Handle data line
            if (line.startsWith('data: ')) {
              const data = line.substring(6).trim();
              
              try {
                if (currentEvent === 'status') {
                  let parsed;
                  try {
                    parsed = JSON.parse(data);
                  } catch (parseError) {
                    continue; // Skip this event if parsing fails
                  }
                  
                  // Validate parsed data structure
                  if (!parsed || typeof parsed !== 'object') {
                    continue;
                  }
                  
                  // CRITICAL: Don't save job ID from stream status updates Job IDs should only be saved when explicitly set via setActiveJobId event (in Dashboard.jsx) This prevents new workflows from inheriting job IDs from stream updates of previous workflows The stream may still be connected for a previous workflow's job ID when switching to a new workflow If we save here, we'd be saving the previous workflow's job ID to the new workflow Removed: saveWorkflowLastJobId(activeWorkflowSessionId, parsed.id);
                  
                  // If status is completed with results, extract and set the data This is the final data from the stream (comes after results_ready)
                  if (parsed.status === "completed" && parsed.result && Array.isArray(parsed.result) && parsed.result.length > 0) {
                    setThinking("");
                    setIsThinking(false);
                    
                    // CRITICAL: Find which workflow this jobId belongs to If jobId doesn't belong to active workflow, switch to correct workflow first
                    const currentState = store.getState();
                    const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
                    const jobOwnerWorkflowId = jobId ? findWorkflowByJobId(jobId) : null;
                    
                    // If jobId belongs to a different workflow, switch to it first
                    if (jobOwnerWorkflowId && jobOwnerWorkflowId !== currentActiveWorkflowSessionId) {
                      dispatch(switchWorkflowSession(jobOwnerWorkflowId));
                      // Update activeWorkflowSessionId for subsequent operations Note: We'll use jobOwnerWorkflowId instead of activeWorkflowSessionId below
                    }
                    
                    // Use the workflow that owns this jobId (or active if jobId not found)
                    const targetWorkflowSessionId = jobOwnerWorkflowId || currentActiveWorkflowSessionId;
                    
                    // Extract and validate results
                    const resultData = parsed.result;
                    const validResults = resultData.filter(r => r !== null && r !== undefined && typeof r === 'object' && Object.keys(r).length > 0);
                    
                    if (validResults.length > 0) {
                      // CRITICAL: Double-check that we're still on the correct workflow
                      // This prevents data from updating the wrong workflow if user switched during stream
                      const currentStateAfterCheck = store.getState();
                      const currentActiveWorkflowAfterCheck = currentStateAfterCheck?.workflowSessions?.activeSessionId;
                      
                      // Only update data if the target workflow is still active
                      const canUpdateData = targetWorkflowSessionId === currentActiveWorkflowAfterCheck;
                      
                      if (!canUpdateData) {
                        // Workflow was switched away - don't update data
                        // Status update will happen below, just skip data updates here
                      } else {
                        // CRITICAL: Only update if this data belongs to the target workflow Check if user has explicitly made changes (isDirty flag) - but only for the target workflow
                        const shouldUpdate = !isDirty || !extractedData || extractedData.length === 0;
                        
                        if (shouldUpdate) {
                          // Dispatch immediately to Redux - this triggers all subscribers Tab data will be saved automatically via tabStorage, no need for separate workflow storage
                          dispatch(setExtractedData(validResults));
                          dispatch(setEditableData(validResults)); // CRITICAL: Also update global editableData for backward compatibility
                          
                          // CRITICAL: Also save to the target workflow's session state and validate immediately
                          if (targetWorkflowSessionId) {
                            // Validate immediately after extraction
                            const { validateRequiredFieldsOnly } = require('../utils/validationUtils');
                            const currentState = store.getState();
                            const isSpanish = currentState?.validationErrors?.isSpanish ?? true;
                            const errors = validateRequiredFieldsOnly(validResults, isSpanish);
                            
                            dispatch(updateWorkflowSessionState(targetWorkflowSessionId, {
                              extractedData: validResults,
                              editableData: validResults,
                              stepsCompleted: true,
                              validationErrors: errors, // Store validation errors immediately
                            }));
                            
                            // PERFORMANCE: Clear PDFs from workflow cache after processing completes
                            // This frees up memory since PDFs are no longer needed after processing
                            clearWorkflowFiles(targetWorkflowSessionId);
                          }
                          
                          // PDFs are stored only in Redux state (localStorage disabled to avoid quota issues) No need to store PDFs in localStorage - they're already in Redux
                        }
                        
                        // OPTIMIZED: Extract ocrInputId from result array items (single pass, no duplicate checks) The ocr_input_id is inside each result item, not at the top level
                        let ocrInputId = parsed.ocr_input_id || parsed.ocrInputId;
                        if (!ocrInputId && validResults.length > 0) {
                          const firstResult = validResults[0];
                          ocrInputId = firstResult?.ocr_input_id || firstResult?.ocrInputId;
                        }
                        
                        // OPTIMIZED: Only dispatch if value changed and we have a valid ID
                        if (ocrInputId) {
                          const ocrInputIdStr = String(ocrInputId).trim();
                          const hasChanged = ocrInputIdStr !== storedOcrInputIdRef.current || 
                                            activeWorkflowSessionId !== storedWorkflowSessionIdRef.current;
                          
                          if (ocrInputIdStr && hasChanged) {
                            // Batch updates: dispatch both in sequence (Redux batches automatically)
                            dispatch(setOcrInputID(ocrInputIdStr));
                            
                            if (activeWorkflowSessionId) {
                              dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
                                ocrInputID: ocrInputIdStr,
                              }));
                            }
                            
                            // Update refs to prevent duplicate dispatches
                            storedOcrInputIdRef.current = ocrInputIdStr;
                            storedWorkflowSessionIdRef.current = activeWorkflowSessionId;
                          }
                        }
                      }
                    }
                  }
                  
                  // Update status - always allow status updates for UI feedback Data updates are handled separately above and respect isDirty flag
                  setStatus(parsed);
                  // If status changes to processing, might start new thinking
                  if (parsed.stage === 'stream_connected' || parsed.stage === 'connecting_to_model') {
                    // New stream starting, clear previous thinking
                    setThinking("");
                    setIsThinking(false);
                  }
                } else if (currentEvent === 'results_ready') {
                  try {
                    let parsed;
                    try {
                      parsed = JSON.parse(data);
                    } catch (parseError) {
                      continue; // Skip this event if parsing fails
                    }
                    
                    // Validate parsed data structure
                    if (!parsed || typeof parsed !== 'object') {
                      continue;
                    }
                    
                    const { results, ocrInputId } = parsed || {};
                    
                    // Validate results data structure with fallbacks
                    let validResults = null;
                    if (results) {
                      if (Array.isArray(results)) {
                        // Filter out null/undefined/empty objects
                        const filteredResults = results.filter(r => r !== null && r !== undefined && typeof r === 'object' && Object.keys(r).length > 0);
                        if (filteredResults.length > 0) {
                          validResults = filteredResults;
                        }
                      } else if (typeof results === 'object' && results !== null && Object.keys(results).length > 0) {
                        // Single object, convert to array
                        validResults = [results];
                      }
                    }
                    
                    // Fallback: Check tab data for workflow (workflow data comes from tabs, not separate storage) Tab data is loaded automatically via tabStorage, so we don't need to check here The extractedData in Redux is the source of truth
                    
                    // CRITICAL: Find which workflow this jobId belongs to If jobId doesn't belong to active workflow, switch to correct workflow first
                    const currentState = store.getState();
                    const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
                    const jobOwnerWorkflowId = jobId ? findWorkflowByJobId(jobId) : null;
                    
                    // If jobId belongs to a different workflow, switch to it first
                    if (jobOwnerWorkflowId && jobOwnerWorkflowId !== currentActiveWorkflowSessionId) {
                      dispatch(switchWorkflowSession(jobOwnerWorkflowId));
                    }
                    
                    // Use the workflow that owns this jobId (or active if jobId not found)
                    const targetWorkflowSessionId = jobOwnerWorkflowId || currentActiveWorkflowSessionId;
                    
                    // CRITICAL: Always update Redux with stream data from results_ready event This is the primary data source - only skip if user has explicitly made changes
                    if (validResults && Array.isArray(validResults) && validResults.length > 0) {
                      // Only skip update if user has made changes (isDirty flag)
                      const shouldUpdate = !isDirty || !extractedData || extractedData.length === 0;
                      
                      if (shouldUpdate) {
                        // Dispatch immediately to Redux - this is the single source of truth Tab data will be saved automatically via tabStorage, no need for separate workflow storage
                        dispatch(setExtractedData(validResults));
                        dispatch(setEditableData(validResults)); // CRITICAL: Also update global editableData for backward compatibility
                        
                        // CRITICAL: Also save to the target workflow's session state and validate immediately
                        if (targetWorkflowSessionId) {
                          // Validate immediately after extraction
                          const { validateRequiredFieldsOnly } = require('../utils/validationUtils');
                          const currentState = store.getState();
                          const isSpanish = currentState?.validationErrors?.isSpanish ?? true;
                          const errors = validateRequiredFieldsOnly(validResults, isSpanish);
                          
                          dispatch(updateWorkflowSessionState(targetWorkflowSessionId, {
                            extractedData: validResults,
                            editableData: validResults,
                            stepsCompleted: true,
                            validationErrors: errors, // Store validation errors immediately
                          }));
                          
                          // PERFORMANCE: Clear PDFs from workflow cache after processing completes
                          // This frees up memory since PDFs are no longer needed after processing
                          clearWorkflowFiles(targetWorkflowSessionId);
                        }
                        
                        // PDFs are stored only in Redux state (localStorage disabled to avoid quota issues) No need to store PDFs in localStorage - they're already in Redux
                      }
                    }
                    
                    // OPTIMIZED: Extract ocrInputId from results array if not in parsed object
                    let extractedOcrInputId = ocrInputId;
                    if (!extractedOcrInputId && validResults && validResults.length > 0) {
                      const firstResult = validResults[0];
                      extractedOcrInputId = firstResult?.ocr_input_id || firstResult?.ocrInputId;
                    }
                    
                    // OPTIMIZED: Only dispatch if value changed and we have a valid ID Use targetWorkflowSessionId (the workflow that owns this jobId)
                    if (extractedOcrInputId && targetWorkflowSessionId) {
                      const ocrInputIdStr = String(extractedOcrInputId).trim();
                      const hasChanged = ocrInputIdStr !== storedOcrInputIdRef.current || 
                                        targetWorkflowSessionId !== storedWorkflowSessionIdRef.current;
                      
                      if (ocrInputIdStr && hasChanged) {
                        // Batch updates: dispatch both in sequence (Redux batches automatically)
                        dispatch(setOcrInputID(ocrInputIdStr));
                        
                        dispatch(updateWorkflowSessionState(targetWorkflowSessionId, {
                          ocrInputID: ocrInputIdStr,
                        }));
                        
                        // Update refs to prevent duplicate dispatches
                        storedOcrInputIdRef.current = ocrInputIdStr;
                        storedWorkflowSessionIdRef.current = targetWorkflowSessionId;
                      }
                    }
                    
                    // Set status to indicate document processing is complete
                    const newStatus = {
                      id: status?.id || jobId,
                      status: 'completed',
                      result: validResults || results, // Use validated results
                      ocrInputId: extractedOcrInputId || null,
                      stage: status?.stage || 'results_ready'
                    };
                    setStatus(newStatus);
                    
                    // Clear thinking when document is ready
                    setThinking("");
                    setIsThinking(false);
                    
                    // Don't close stream here - wait for stream_closed event The stream will continue to receive status events until stream_closed
                    
                    // Play sound, show notification, and change tab title (once per job id per page load)
                    const completionKey = newStatus.id || jobId;
                    if (consumeJobCompletionFeedbackOnce(completionKey)) {
                      const isSpanish = getLanguage();
                      
                      // Play sound notification
                      playNotificationSound();
                      
                      // Show browser notification
                      showBrowserNotification(
                        getProcessingModalTranslation('documentsProcessed', isSpanish),
                        getProcessingModalTranslation('processingDoneNotification', isSpanish)
                      );
                      
                      // Change tab title
                      if (!originalTitleRef.current) {
                        originalTitleRef.current = document.title;
                      }
                      const doneTitle = getProcessingModalTranslation('processingDone', isSpanish);
                      document.title = `✓ ${doneTitle} - ${originalTitleRef.current}`;
                      
                      // Clean up any existing handlers first
                      restoreOriginalTitle();
                      
                      // Restore original title when user comes back to the tab (visibility change)
                      const handleVisibilityChange = () => {
                        if (!document.hidden) {
                          restoreOriginalTitle();
                        }
                      };
                      document.addEventListener('visibilitychange', handleVisibilityChange);
                      titleRestoreHandlersRef.current.visibility = handleVisibilityChange;
                      
                      // Also restore title on window focus
                      const handleFocus = () => {
                        restoreOriginalTitle();
                      };
                      window.addEventListener('focus', handleFocus);
                      titleRestoreHandlersRef.current.focus = handleFocus;
                      
                      // Also restore title on any click (user interaction)
                      const handleClick = () => {
                        restoreOriginalTitle();
                      };
                      document.addEventListener('click', handleClick, { once: true });
                      titleRestoreHandlersRef.current.click = handleClick;
                    }
                  } catch (e) {
                  }
                } else if (currentEvent === 'thinking_delta') {
                  // When thinking starts, clear previous thinking if we were in text mode
                  if (lastEventTypeRef.current === 'text_delta' || lastEventTypeRef.current === '') {
                    setThinking("");
                    setCurrentThinkingSection("");
                    thinkingBufferRef.current = "";
                    lastSectionStartRef.current = -1;
                  }
                  setIsThinking(true);
                  // Parse delta JSON format: {"delta":"text"}
                  try {
                    let deltaData;
                    try {
                      deltaData = JSON.parse(data);
                    } catch (parseError) {
                      // If not JSON, use data as-is
                      deltaData = { delta: data };
                    }
                    const delta = deltaData.delta !== undefined ? deltaData.delta : data;
                    
                    // Add to buffer
                    thinkingBufferRef.current += delta;
                    
                    // Detect section boundaries - extract only the most recent section
                    const buffer = thinkingBufferRef.current;
                    
                    // Look for section markers with more specific patterns Match: **Document 1, **Document 2, **Invoice EXP2500550, **Shipping, **Product, etc.
                    const sectionPatterns = [
                      /\*\*Document \d+/g,  // **Document 1, **Document 2, etc.
                      /\*\*Invoice [A-Z0-9]+/g,  // **Invoice EXP2500550
                      /\*\*Shipping/g,
                      /\*\*Product/g,
                    ];
                    
                    let currentSectionStart = -1;
                    
                    // Find the most recent section marker
                    for (const pattern of sectionPatterns) {
                      const matches = [...buffer.matchAll(pattern)];
                      if (matches.length > 0) {
                        const lastMatch = matches[matches.length - 1];
                        const matchIndex = lastMatch.index || 0;
                        if (matchIndex > currentSectionStart) {
                          currentSectionStart = matchIndex;
                        }
                      }
                    }
                    
                    // Also detect sections by spacing (double newlines or significant spacing) Look for patterns like \n\n or \n\n\n which indicate new sections
                    const spacingPattern = /\n\n+/g;
                    const spacingMatches = [...buffer.matchAll(spacingPattern)];
                    if (spacingMatches.length > 0) {
                      const lastSpacingMatch = spacingMatches[spacingMatches.length - 1];
                      const spacingIndex = (lastSpacingMatch.index || 0) + (lastSpacingMatch[0].length || 0);
                      // Only use spacing if it's more recent than the last section marker
                      if (spacingIndex > currentSectionStart) {
                        currentSectionStart = spacingIndex;
                      }
                    }
                    
                    // Check if we've moved to a new section (section start position changed)
                    const isNewSection = currentSectionStart !== lastSectionStartRef.current && 
                                       currentSectionStart >= 0 && 
                                       lastSectionStartRef.current >= 0;
                    
                    // If it's a new section, trigger fade animation
                    if (isNewSection) {
                      setSectionKey(prev => prev + 1);
                      lastSectionStartRef.current = currentSectionStart;
                    } else if (lastSectionStartRef.current === -1 && currentSectionStart >= 0) {
                      // First section detected
                      lastSectionStartRef.current = currentSectionStart;
                    } else if (lastSectionStartRef.current === -1) {
                      // No section markers yet, use 0 as start
                      lastSectionStartRef.current = 0;
                    }
                    
                    // Determine the current section content
                    let currentSection = "";
                    if (currentSectionStart >= 0) {
                      currentSection = buffer.substring(currentSectionStart).trim();
                    } else {
                      // No section markers found yet, show all content
                      currentSection = buffer;
                    }
                    
                    setCurrentThinkingSection(currentSection);
                    setThinking(currentSection);
                  } catch (e) {
                    // If not JSON, use data as-is
                    thinkingBufferRef.current += data;
                    setCurrentThinkingSection(thinkingBufferRef.current);
                    setThinking(thinkingBufferRef.current);
                  }
                  lastEventTypeRef.current = 'thinking_delta';
                } else if (currentEvent === 'text_delta') {
                  // When text starts, clear thinking and stop thinking mode
                  if (lastEventTypeRef.current === 'thinking_delta') {
                    setThinking("");
                    setCurrentThinkingSection("");
                    thinkingBufferRef.current = "";
                    lastSectionStartRef.current = -1;
                    setIsThinking(false);
                  }
                  // Parse delta JSON format: {"delta":"text"}
                  try {
                    let deltaData;
                    try {
                      deltaData = JSON.parse(data);
                    } catch (parseError) {
                      // If not JSON, use data as-is
                      deltaData = { delta: data };
                    }
                    
                    if (deltaData && typeof deltaData === 'object' && deltaData.delta !== undefined) {
                      setText((prev) => (prev || '') + deltaData.delta);
                    } else {
                      setText((prev) => (prev || '') + (data || ''));
                    }
                  } catch (e) {
                    // If not JSON, use data as-is
                    setText((prev) => (prev || '') + (data || ''));
                  }
                  lastEventTypeRef.current = 'text_delta';
                } else if (currentEvent === 'usage') {
                  if (process.env.NODE_ENV === 'development') {
                    try {
                      setUsage(JSON.parse(data));
                    } catch (e) {
                      setUsage(data);
                    }
                  }
                } else if (currentEvent === 'stream_closed') {
                  // Stream is closed - this is the final event, close the modal
                  setStatus((prev) => ({
                    ...prev,
                    status: 'completed',
                    stage: 'stream_closed'
                  }));
                  // Clear thinking
                  setThinking("");
                  setIsThinking(false);
                  // Close the stream connection
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                    abortControllerRef.current = null;
                  }
                  // Clear sessionStorage items
                  sessionStorage.removeItem('activeJobId');
                  // Dispatch event to clear activeJobId in Dashboard
                  window.dispatchEvent(new CustomEvent('clearActiveJobId'));
                  // Break out of the stream loop
                  break;
                } else {
                  // Default: try to parse as JSON and handle based on content
                  try {
                    let parsed;
                    try {
                      parsed = JSON.parse(data);
                    } catch (parseError) {
                      // If not JSON, treat as text
                      setText((prev) => (prev || '') + (data || ''));
                      continue;
                    }
                    
                    // Validate parsed data
                    if (!parsed || typeof parsed !== 'object') {
                      setText((prev) => (prev || '') + (data || ''));
                      continue;
                    }
                    
                    // If it looks like a status object
                    if (parsed.status !== undefined || parsed.stage !== undefined) {
                      setStatus(parsed);
                    } 
                    // If it has a delta field, treat as text_delta
                    else if (parsed.delta !== undefined) {
                      setText((prev) => (prev || '') + parsed.delta);
                    } else {
                      // Unknown format, log and treat as text 
                      setText((prev) => (prev || '') + (data || ''));
                    }
                  } catch (e) {
                    // If not JSON, treat as text
                    setText((prev) => (prev || '') + (data || ''));
                  }
                }
              } catch (error) {
                // If parsing fails, treat based on event type
                if (currentEvent === 'thinking_delta') {
                  try {
                    const deltaData = JSON.parse(data);
                    setThinking((prev) => prev + (deltaData.delta || data));
                  } catch (e) {
                    setThinking((prev) => prev + data);
                  }
                } else {
                  try {
                    const deltaData = JSON.parse(data);
                    setText((prev) => prev + (deltaData.delta || data));
                  } catch (e) {
                    setText((prev) => prev + data);
                  }
                }
              }
            } else if (line.trim() === '') {
              // Empty line indicates end of event, reset currentEvent and currentId
              currentEvent = '';
              currentId = '';
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
        } else {
        }
      }
    };

    streamData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Clean up title restore handlers
      restoreOriginalTitle();
    };
  }, [jobId, dispatch]);

  return { status, thinking, text, usage, isThinking, sectionKey };
}

